/**
 * EnhancedAppHeader - Portal header with three rendering modes:
 *   Anonymous        → logo + Login button + LanguageSwitcher
 *   AuthLogoutOnly   → logo + LogoutOnlyUserMenu (blocked/unpaid users)
 *   AuthFull         → all widgets right (default; NetworkSelector lives in sidebar)
 */

import { LogoutCommand } from '@/application/commands/auth';
import { logger, mediator } from '@/application/core';
import { NS } from '@/i18n/namespaces';
import { AppHeader, Button } from '@/components/ui';
import { LogOut } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FavouritesWidget } from '../FavouritesWidget';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { MobileHeaderMenu } from '../MobileHeaderMenu';
import { NotificationWidget } from '../NotificationWidget';
import { navigatePortalInternal, startPortalNavigation } from '../PortalLayout/PortalLayout.store';
import { TenantInfoDropdown } from '../TenantInfoDropdown';
import { useHasAnyRole } from '../UserContextProvider/use-has-role';
import {
  useCsrfToken,
  useDisplayName,
  useIsAnonymous,
  useIsLogoutOnly,
} from '../UserContextProvider/UserContextProvider.store';
import { UserMenu } from '../UserMenu';

export interface EnhancedAppHeaderProps {
  onToggleSidebar?: () => void;
  showSidebarToggle?: boolean;
  className?: string;
}

// ── Inline sub-components ───────────────────────────────────────────────────

function AnonymousHeaderLeft() {
  return <img src="/img/ecgridlogo2x.png" alt="ECGrid" className="h-8 lg:h-10 w-auto" />;
}

function AnonymousHeaderRight() {
  return (
    <div className="flex items-center gap-3">
      <LanguageSwitcher className="w-auto min-w-[80px]" />
    </div>
  );
}

function LogoutOnlyUserMenu() {
  const { t } = useTranslation(NS.COMMON);
  const csrfToken = useCsrfToken();
  const rawDisplayName = useDisplayName();
  const displayName = rawDisplayName || t('UserMenu_DefaultDisplayName', { defaultValue: 'User' });

  const handleLogout = async () => {
    if (!startPortalNavigation()) return;
    try {
      await mediator.send(new LogoutCommand(csrfToken));
    } catch (error) {
      logger.error('LogoutOnlyUserMenu', 'Logout error:', error);
    } finally {
      const redirected = navigatePortalInternal('/', { allowWhileNavigating: true, allowSameUrl: true });
      if (!redirected) window.location.href = '/';
    }
  };

  return (
    <Button
      variant="ghost"
      onClick={handleLogout}
      className="min-w-8 h-8"
    >
      <div className="flex items-center space-x-2">
        <span>{displayName}</span>
        <LogOut className="h-4 w-4" />
        <span className="sr-only">{t('UserMenu_Logout', { defaultValue: 'Logout' })}</span>
      </div>
    </Button>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

const EnhancedAppHeaderComponent = React.forwardRef<HTMLElement, EnhancedAppHeaderProps>(
  ({ onToggleSidebar, showSidebarToggle = true, className }, ref) => {
    const isAnonymous = useIsAnonymous();
    const isLogoutOnly = useIsLogoutOnly();
    const canSeeFavourites = useHasAnyRole('Root', 'NetOps', 'TechOps', 'NetworkAdmin');

    const authRightSlot = useMemo(
      () => (
        <div className="flex items-center gap-1 md:gap-3">
          <div className="hidden md:flex items-center gap-3">
            <TenantInfoDropdown />
            <div className="h-8 w-px bg-border" />
          </div>
          {canSeeFavourites && (
            <div className="hidden md:block">
              <FavouritesWidget />
            </div>
          )}
          <div className="hidden md:block">
            <NotificationWidget />
          </div>
          <div className="hidden md:flex items-center gap-3">
            <div className="h-8 w-px bg-border" />
            <LanguageSwitcher className="w-auto min-w-[80px]" />
            <div className="h-8 w-px bg-border" />
          </div>
          <div className="md:hidden">
            <MobileHeaderMenu />
          </div>
          <div className="hidden md:block">
            <UserMenu />
          </div>
        </div>
      ),
      [canSeeFavourites]
    );

    const slots = useMemo(() => {
      if (isAnonymous) return { left: <AnonymousHeaderLeft />, right: <AnonymousHeaderRight /> };
      if (isLogoutOnly) return { right: <LogoutOnlyUserMenu /> };
      return { right: authRightSlot };
    }, [isAnonymous, isLogoutOnly, authRightSlot]);

    return (
      <AppHeader
        ref={ref}
        onToggleSidebar={onToggleSidebar}
        showSidebarToggle={isAnonymous || isLogoutOnly ? false : showSidebarToggle}
        sticky={true}
        className={className}
        slots={slots}
      />
    );
  }
);

EnhancedAppHeaderComponent.displayName = 'EnhancedAppHeader';

export const EnhancedAppHeader = React.memo(EnhancedAppHeaderComponent);
